import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { InvoiceForm } from '@/components/finances/InvoiceForm';
import { invoicesService } from '@/services/invoices.service';
import type { Invoice } from '@/types';
import { Link } from 'react-router-dom';

function EditInvoiceContent() {
  const { id: invoiceId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (invoiceId) {
      loadInvoice();
    }
  }, [invoiceId]);

  const loadInvoice = async () => {
    if (!invoiceId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await invoicesService.getInvoice(invoiceId);

      // Check if invoice can be edited
      if (data.status !== 'draft') {
        setError('Only draft invoices can be edited');
        return;
      }

      setInvoice(data);
    } catch (err: any) {
      console.error('Error loading invoice:', err);
      setError(err.message || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = (invoice: Invoice) => {
    navigate(`/finances/invoices/${invoice.id}`);
  };

  const handleCancel = () => {
    navigate(`/finances/invoices/${invoiceId}`);
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="border border-border rounded-lg p-4 text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-flowky-primary"></div>
          <p className="text-gray-600 mt-4">Loading invoice...</p>
        </div>
      </AppLayout>
    );
  }

  if (error || !invoice) {
    return (
      <AppLayout>
        <div className="border border-border rounded-lg p-4 text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-red-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading invoice</h3>
          <p className="mt-1 text-sm text-gray-500">{error || 'Invoice not found'}</p>
          <div className="mt-4 flex items-center justify-center gap-3">
            <Link
              to={`/finances/invoices/${invoiceId}`}
              className="px-4 py-2 bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90"
            >
              View Invoice
            </Link>
            <Link
              to="/finances/invoices"
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Back to Invoices
            </Link>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="mb-6">
        <Breadcrumb
          items={[
            { label: 'Finances', href: '/finances' },
            { label: 'Invoices', href: '/finances/invoices' },
            { label: invoice.invoiceNumber, href: `/finances/invoices/${invoice.id}` },
            { label: 'Edit' },
          ]}
        />
        <h1 className="text-3xl font-bold text-gray-900 mt-2">Edit Invoice</h1>
        <p className="text-gray-600 mt-1">Update invoice details</p>
      </div>

      <InvoiceForm
        mode="edit"
        initialData={invoice}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
      />
    </AppLayout>
  );
}

export default function EditInvoicePage() {
  return (
    <ProtectedRoute>
      <EditInvoiceContent />
    </ProtectedRoute>
  );
}
